/**
 * KPI — tile chiffrée (anticipée pour le dashboard S6).
 *
 * Primitive DS : un titre court + un grand chiffre + une légende facultative.
 * Variante `accent` rouge éditorial pour les chiffres marquants.
 *
 *   <KPI label="Œuvres publiées" value={42} legend="ce mois" />
 *   <KPI label="Ventes" value="2 480 €" variant="accent" />
 *
 * En S3, le composant est utilisé en bandeau "Drafts pending" sur l'onglet
 * Contenu généré pour donner un aperçu chiffré rapide.
 */

import type { ReactNode } from 'react'

interface KPIProps {
  label: string
  value: ReactNode
  legend?: ReactNode
  variant?: 'default' | 'accent' | 'muted'
  /**
   * Si fourni, rend la KPI cliquable (button stylé sans bordure). Sinon,
   * c'est une div informative passive — par défaut.
   */
  onClick?: () => void
  className?: string
}

export default function KPI({
  label,
  value,
  legend,
  variant = 'default',
  onClick,
  className,
}: KPIProps) {
  const classes = [
    'ui-kpi',
    variant !== 'default' && `ui-kpi--${variant}`,
    onClick && 'ui-kpi--clickable',
    className,
  ]
    .filter(Boolean)
    .join(' ')

  const content = (
    <>
      <span className="ui-kpi-label">{label}</span>
      <span className="ui-kpi-value">{value}</span>
      {legend && <span className="ui-kpi-legend">{legend}</span>}
    </>
  )

  if (onClick) {
    return (
      <button type="button" className={classes} onClick={onClick}>
        {content}
      </button>
    )
  }
  return <div className={classes}>{content}</div>
}
